import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Data } from '../data/Data';
import { useNotificationsManager } from './notificationContext';

const MessageContext = createContext();
const MessageManagerContext = createContext();

export const MessageContextProvider = ({ children }) => {

    const { dismiss, extractErrorMessages, inform } = useNotificationsManager();
    const location = useLocation();
    const { pathname } = location;

    const [messages, setMessages] = useState([]);

    const totalUnreadMessages = useMemo(
        () => {
            if (!Array.isArray(messages)) return 0;
            else return messages.filter(message => !!message.loginCompte && !message.lu).length;
        },
        [messages]
    );

    const markAllAsSeen = useCallback(() => {
        if (totalUnreadMessages > 0) {
            Data._markAllMessageAsRead().then(response => {
                if (response.codeRetour === 0) {
                    setMessages(prev => prev.map(message => {
                        const { loginCompte } = message;
                        if (!!loginCompte) return { ...message, lu: true };
                        else return message;
                    }));
                }
            });
        }
    }, [totalUnreadMessages]);

    useEffect(() => {
        if (pathname === "/messages" && totalUnreadMessages > 0)
            markAllAsSeen();
    }, [markAllAsSeen, pathname, totalUnreadMessages]);

    const addMessage = useCallback((message) => {
        setMessages(prev => [...prev, message]);
    }, []);

    const resetMessages = useCallback(() => {
        setMessages([]);
    }, []);

    const loadMessages = useCallback(() => {
        const id = inform("Chargement des messages...");

        Data._getMessages().then(response => {
            if (response.codeRetour === 0) {
                const objet = response.reponseAPIobjet;
                let messages = objet.messages || [];
                messages = messages.sort((a, b) => new Date(a.dateEnvoi) - new Date(b.dateEnvoi));
                setMessages(messages);
            }
            else extractErrorMessages(response);

            dismiss(id);
        });
    }, [dismiss, extractErrorMessages, inform]);

    const value = useMemo(
        () => ({ messages, totalUnreadMessages }),
        [messages, totalUnreadMessages]
    );

    const managerValue = useMemo(
        () => ({ addMessage, loadMessages, resetMessages }),
        [addMessage, loadMessages, resetMessages]
    );

    return <MessageManagerContext.Provider value={managerValue}>
        <MessageContext.Provider value={value}>
            {children}
        </MessageContext.Provider>
    </MessageManagerContext.Provider>;

};

export const useMessages = () => {
    const data = React.useContext(MessageContext);
    if (data === undefined) throw new Error();
    const { messages, totalUnreadMessages } = data;
    return { messages, totalUnreadMessages };
};

export const useMessagesManager = () => {
    const manager = React.useContext(MessageManagerContext);
    if (manager === undefined) throw new Error();
    const { addMessage, loadMessages, resetMessages } = manager;
    return { addMessage, loadMessages, resetMessages };
};